import { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Github, ExternalLink, Tag } from 'lucide-react';
import { readData } from '@/firebase/database';

interface Project {
  id?: string;
  title: string;
  description: string;
  image: string;
  tags: string[];
  github: string;
  live: string;
  featured: boolean;
}

const ProjectCard = ({ project, index }: { project: Project; index: number }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.15 }} className="glass neon-border rounded-2xl overflow-hidden card-hover group">
      <div className="relative h-36 sm:h-48 overflow-hidden">
        <img src={project.image} alt={project.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-card via-card/20 to-transparent" />
        {project.featured && (
          <div className="absolute top-2 sm:top-3 left-2 sm:left-3">
            <span className="bg-primary text-primary-foreground text-[10px] sm:text-xs font-mono font-bold px-2 py-0.5 sm:py-1 rounded-full">⭐ Featured</span>
          </div>
        )}
      </div>
      <div className="p-4 sm:p-6">
        <h3 className="font-display text-base sm:text-xl font-bold mb-1.5 sm:mb-2 group-hover:text-primary transition-colors duration-300">{project.title}</h3>
        <p className="text-muted-foreground text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4 line-clamp-2">{project.description}</p>
        <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-4 sm:mb-5">
          {project.tags?.map((tag) => (
            <span key={tag} className="flex items-center gap-1 text-[10px] sm:text-xs font-mono px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-md bg-primary/10 text-primary border border-primary/20">
              <Tag size={8} /> {tag}
            </span>
          ))}
        </div>
        <div className="flex gap-3">
          <a href={project.github} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors duration-300">
            <Github size={14} /> <span>Code</span>
          </a>
          <a href={project.live} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs sm:text-sm btn-neon py-1 sm:py-1.5 px-3 sm:px-4 ml-auto">
            <ExternalLink size={12} /> Live Demo
          </a>
        </div>
      </div>
    </motion.div>
  );
};

const Projects = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [filter, setFilter] = useState('All');
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  useEffect(() => {
    readData('projects').then((data) => {
      if (data) {
        const list: Project[] = Object.entries(data).map(([id, val]) => ({ id, ...(val as Omit<Project, 'id'>) }));
        setProjects(list);
      }
    });
  }, []);

  const allTags = ['All', 'Featured', ...Array.from(new Set(projects.flatMap((p) => p.tags || [])))];
  const filtered = filter === 'All' ? projects : filter === 'Featured' ? projects.filter((p) => p.featured) : projects.filter((p) => p.tags?.includes(filter));

  return (
    <section id="projects" className="py-16 sm:py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6" ref={ref}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }} className="text-center mb-10 sm:mb-16">
          <p className="section-tag text-xs">What I've built</p>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold">
            My <span className="gradient-text">Projects</span>
          </h2>
          <div className="w-20 h-0.5 mx-auto mt-4 bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.2 }}
          className="flex flex-wrap gap-2 justify-center mb-8 sm:mb-10">
          {allTags.slice(0, 8).map((tag) => (
            <button key={tag} onClick={() => setFilter(tag)}
              className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-mono transition-all duration-300 ${
                filter === tag ? 'bg-primary text-primary-foreground font-bold' : 'glass border border-border text-muted-foreground hover:border-primary/30 hover:text-foreground'
              }`}>{tag}</button>
          ))}
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {filtered.map((project, i) => <ProjectCard key={project.id} project={project} index={i} />)}
        </div>
      </div>
    </section>
  );
};

export default Projects;
